/**
 * `PlaylistCard Container`
 * @param {IPlaylist} playlist optionals components.
 * @returns {JSX.Element} Component.
 */
import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AccountBadgeWrapper,
  AccountHeaderLogo,
  ActionButtonsSlot,
  AssignedMessage,
  CardFooter,
  Container,
  Content,
  ContentWrapper,
  DateContainer,
  Description,
  Divider,
  DurationPill,
  Header,
  InfoIcon,
  InfoWrapper,
  PlaylistInfo,
  RecommendedDescription,
  RecommendedDescriptionContainer,
  TitleRow,
  TitleText,
} from './styles';
import { IContentGroup, IPlaylist } from 'contexts/playlists/types';
import { ReactComponent as PlaylistIcon } from 'assets/icons/playlist-icon.svg';
import { durationFromSecs } from 'utils/helpers';
import ActionButtons from './ActionButtons';
import {
  ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS,
  ADD_USERS_LABEL,
  ADD_USERS_ONLY_LABEL,
  ALL_USERS_ASSIGNED_TOOLTIP,
  PLAYLIST_TYPE,
} from 'constants/playlists';
import SonarPlaylistLogo from 'assets/images/sonar-playlist.png';
import AvatarLetter from 'components/avatar/AvatarLetter';
import Icon from 'components/icons';
import Tooltip from 'components/tooltip';
import AccountPlaylistToggle, {
  AccountPlaylistBadge,
  type AccountPlaylistToggleState,
} from '../AccountPlaylistToggle';
import { SessionContext } from 'contexts/session';
import { PlaylistsContext } from 'contexts/playlists';
import AccountDefaultLogo from 'assets/images/account-avatar.svg';
import Notification from 'components/notification';
import { getAccountPlaylistCooldownSecondsRemaining } from '../AccountPlaylistToggle/AccountPlaylistToggle.state';
import {
  type TooltipPlacement,
  getPlaylistCardTooltipPlacements,
  measureTextHeight,
} from 'utils/tooltipPlacement';

type Props = {
  playlist: IPlaylist;
  onAddUsers: (playlist?: IPlaylist) => void;
  onDeletePlaylist: (playlist?: IPlaylist) => void;
  onEditPlaylist: (playlist?: IPlaylist) => void;
  onClonePlaylist: (playlist?: IPlaylist) => void;
  onAssignUser: (playlist?: IPlaylist) => void;
  onUnassignUser: (playlist?: IPlaylist) => void;
  hideClone?: boolean;
};

const contentLength = (
  content: IContentGroup[],
  contentCount?: number,
  activitiesCount?: number
): string => {
  let quantity = 0;
  const resolvedCount = typeof activitiesCount === 'number' ? activitiesCount : contentCount;

  if (typeof resolvedCount === 'number') {
    quantity = resolvedCount;
  } else {
    for (const group of content) {
      quantity = quantity + group.content.length;
    }
  }

  return quantity === 1 ? `${quantity} Activity` : `${quantity} Activities`;
};

const PlaylistCard = ({
  playlist,
  onAddUsers,
  onDeletePlaylist,
  onEditPlaylist,
  onClonePlaylist,
  onAssignUser,
  onUnassignUser,
  hideClone,
}: Props): JSX.Element => {
  const history = useHistory();
  const session = useContext(SessionContext);
  const playlistsContext = useContext(PlaylistsContext);
  const { dashboardAccountPlaylistAssignment, dashboardTeamsPlaylistAssignment } = useFlags() || {};
  const isAccountPlaylistFeatureEnabled = Boolean(dashboardAccountPlaylistAssignment);
  const isTeamsPlaylistAssignmentEnabled = Boolean(dashboardTeamsPlaylistAssignment);
  const accountLogo = session?.account?.logo_url || AccountDefaultLogo;
  const isAccountOwner = session?.isAccountOwner;
  const isTeamManager = session?.isTeamManager;
  const isAccountPlaylist = Boolean(playlist?.is_account_playlist);
  const assignedUsersCount =
    typeof playlist?.assigned_users_count === 'number'
      ? playlist.assigned_users_count
      : playlist?.assigned_user_ids?.length ?? 0;
  const alreadyAssigned = useMemo(() => {
    return Boolean(playlist?.assigned) || (assignedUsersCount ?? 0) > 0;
  }, [playlist, assignedUsersCount]);

  const isSonarRecommended = PLAYLIST_TYPE.SONAR_RECOMMENDED === playlist.type;
  const isSonarGoal = PLAYLIST_TYPE.SONAR_GOAL === playlist.type;

  const handleGoToPlaylist = (id: IPlaylist['id']) => {
    history.push(`/playlist/${id}`);
  };

  const handleCardClick = () => {
    handleGoToPlaylist(playlist.id);
  };

  const totalAccountUsersRaw = session?.account?.users_count;
  const totalAccountUsers =
    typeof totalAccountUsersRaw === 'number'
      ? totalAccountUsersRaw
      : Number.isFinite(Number(totalAccountUsersRaw))
      ? Number(totalAccountUsersRaw)
      : null;
  const allUsersAssigned =
    !isSonarRecommended &&
    !isSonarGoal &&
    ((isAccountPlaylistFeatureEnabled && isAccountPlaylist) ||
      (typeof totalAccountUsers === 'number' &&
        totalAccountUsers > 0 &&
        assignedUsersCount >= totalAccountUsers));

  const assignedLabel = useMemo(() => {
    if (allUsersAssigned) {
      const totalLabel =
        typeof totalAccountUsers === 'number' && totalAccountUsers > 0
          ? totalAccountUsers
          : assignedUsersCount ?? 0;
      return `All users assigned (${totalLabel} total)`;
    }
    if (!alreadyAssigned) return 'Unassigned';
    if (isSonarRecommended) {
      const isAssigned =
        Boolean(playlist?.assigned) ||
        playlist?.assigned_user_ids?.some((id) => id === playlist?.created_for?.id) ||
        (typeof assignedUsersCount === 'number' ? assignedUsersCount > 0 : false);
      if (isAssigned) return 'Assigned';
    }

    if (typeof assignedUsersCount !== 'number' || assignedUsersCount === 0) {
      return 'Assigned';
    }

    const cappedCount = assignedUsersCount > 99 ? 99 : assignedUsersCount;
    return `Assigned to ${cappedCount}${assignedUsersCount > 99 ? '+' : ''} user${
      cappedCount !== 1 ? 's' : ''
    }`;
  }, [
    allUsersAssigned,
    alreadyAssigned,
    assignedUsersCount,
    isSonarRecommended,
    playlist,
    totalAccountUsers,
  ]);

  const createdBy =
    playlist?.created_by && playlist?.created_by?.id
      ? `${playlist?.created_by.first_name} ${playlist?.created_by.last_name}`
      : ``;

  const createdTime = format(new Date(playlist.created), 'MMM dd, yyyy');

  const modifiedTime = format(new Date(playlist.modified), 'MMM dd, yyyy');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const descriptionRef = useRef(null);
  const descriptionTextRef = useRef(null);
  const headerRef = useRef(null);

  const [toggleCooldownActive, setToggleCooldownActive] = useState(false);
  const [toggleCooldownSecondsRemaining, setToggleCooldownSecondsRemaining] = useState(0);
  const [bothMaxHeight, setBothMaxHeight] = useState(false);
  const [descriptionHeight, setDescriptionHeight] = useState(0);
  const [cardTooltipPlacement, setCardTooltipPlacement] = useState<'top' | 'bottom'>('top');
  const [descriptionTooltipWidth, setDescriptionTooltipWidth] = useState<number | null>(null);
  const [descriptionTooltipPlacement, setDescriptionTooltipPlacement] =
    useState<TooltipPlacement>('bottom');

  const descriptionMaxLines = useMemo(() => {
    const DESCRIPTION_LINE_HEIGHT = 24;
    const calculatedMaxLines = Math.floor(descriptionHeight / DESCRIPTION_LINE_HEIGHT);
    // The UI needs at least 3 lines so desc text doesn't look cramped or clipped.
    return Math.max(calculatedMaxLines || 0, 3);
  }, [descriptionHeight]);

  const descriptionTooltip = useMemo(() => {
    const MAX_SONAR_DESCRIPTION_LENGTH = 43;
    const MAX_DESCRIPTION_LENGTH = 118;
    if (playlist.type === PLAYLIST_TYPE.SONAR_RECOMMENDED) {
      const descriptionText = playlist.description;
      return descriptionText.length > MAX_SONAR_DESCRIPTION_LENGTH ? undefined : false;
    }
    return playlist.description.length > MAX_DESCRIPTION_LENGTH ? undefined : false;
  }, [playlist.description]);

  const titleTooltip = useMemo(() => {
    const MAX_TITLE_LENGTH = 72;
    const titleText = playlist.name;
    return titleText.length > MAX_TITLE_LENGTH ? undefined : false;
  }, [playlist.name]);

  const updateTooltipMetrics = useCallback(() => {
    if (!containerRef.current || typeof window === 'undefined') return;
    const rect = containerRef.current.getBoundingClientRect();
    const descriptionRect = descriptionRef.current?.getBoundingClientRect() ?? rect;
    setDescriptionTooltipWidth(rect.width || null);

    const tooltipWidth = rect.width || 0;
    const tooltipHeight =
      measureTextHeight({
        text: playlist.description ?? '',
        width: tooltipWidth,
        sourceElement: descriptionRef.current,
      }) + 16;

    const { cardTooltipPlacement: nextCardPlacement, descriptionTooltipPlacement } =
      getPlaylistCardTooltipPlacements({
        containerRect: rect,
        anchorRect: descriptionRect,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        tooltipWidth,
        tooltipHeight,
      });

    setCardTooltipPlacement(nextCardPlacement);
    setDescriptionTooltipPlacement(descriptionTooltipPlacement);
  }, [playlist.description]);

  useLayoutEffect(() => {
    setDescriptionHeight(descriptionTextRef?.current?.getBoundingClientRect()?.height);
  }, [playlist.description]);

  useLayoutEffect(() => {
    const MAX_HEIGHT = 126;
    const height = headerRef?.current?.getBoundingClientRect().height;
    setBothMaxHeight(height >= MAX_HEIGHT);
  }, [playlist]);

  useLayoutEffect(() => {
    updateTooltipMetrics();
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', updateTooltipMetrics);
    window.addEventListener('scroll', updateTooltipMetrics, true);
    return () => {
      window.removeEventListener('resize', updateTooltipMetrics);
      window.removeEventListener('scroll', updateTooltipMetrics, true);
    };
  }, [updateTooltipMetrics]);

  // Refresh list data after a toggle so counts/badges stay accurate.
  const refreshPlaylists = playlistsContext?.actions?.refreshPlaylists;
  const handleAccountToggleRefresh = useCallback(async () => {
    if (refreshPlaylists) {
      await refreshPlaylists();
    }
  }, [refreshPlaylists]);

  const handleToggleStateChange = useCallback((state: AccountPlaylistToggleState) => {
    setToggleCooldownActive(state.isCoolingDown);
    setToggleCooldownSecondsRemaining(state.cooldownSecondsRemaining);
  }, []);

  const canShowAccountToggle =
    isAccountPlaylistFeatureEnabled &&
    !isSonarRecommended &&
    !isSonarGoal &&
    Boolean(isTeamManager) &&
    isAccountPlaylist;
  const addUsersLabel = isTeamsPlaylistAssignmentEnabled ? ADD_USERS_LABEL : ADD_USERS_ONLY_LABEL;
  const accountBadgeVisible = isAccountPlaylistFeatureEnabled && isAccountPlaylist;
  const hideAddUsers = isAccountPlaylistFeatureEnabled && isAccountPlaylist;
  // When dashboardTeamsPlaylistAssignment is ON, only account playlists disable the assign button.
  // Manually-assigned playlists keep the button enabled so teams can still be assigned.
  const allAssignedDisablesButton = isTeamsPlaylistAssignmentEnabled
    ? isAccountPlaylistFeatureEnabled && isAccountPlaylist && !isSonarRecommended && !isSonarGoal
    : allUsersAssigned;
  const disableAddUsers =
    allAssignedDisablesButton || (canShowAccountToggle && toggleCooldownActive);
  const addUsersTooltip = useMemo(() => {
    if (canShowAccountToggle && toggleCooldownActive) {
      return ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.cooldown(Math.max(1, toggleCooldownSecondsRemaining));
    }
    return allAssignedDisablesButton ? ALL_USERS_ASSIGNED_TOOLTIP : addUsersLabel;
  }, [
    canShowAccountToggle,
    toggleCooldownActive,
    toggleCooldownSecondsRemaining,
    allAssignedDisablesButton,
    addUsersLabel,
  ]);
  const handleAddUsers = useCallback(
    (playlist: IPlaylist) => {
      if (isAccountPlaylistFeatureEnabled) {
        const remainingSeconds = getAccountPlaylistCooldownSecondsRemaining(playlist?.id);
        if (remainingSeconds > 0) {
          Notification({
            type: 'error',
            text: ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS.cooldown(remainingSeconds),
          });
          return;
        }
      }
      onAddUsers(playlist);
    },
    [isAccountPlaylistFeatureEnabled, onAddUsers]
  );

  return (
    <Tooltip
      destroyTooltipOnHide
      placement={cardTooltipPlacement}
      overlayInnerStyle={{ textAlign: 'center' }}
      title="Open playlist"
    >
      <Container
        ref={containerRef}
        clickable={true}
        onClick={handleCardClick}
        onMouseEnter={updateTooltipMetrics}
      >
        <Header color={playlist.color} isSonar={isSonarRecommended}>
          {playlist.duration_in_seconds > 0 && (
            <DurationPill bgColor="#57575C">
              {durationFromSecs(playlist.duration_in_seconds)}
            </DurationPill>
          )}
          {isSonarRecommended ? (
            <img width="170px" src={SonarPlaylistLogo} alt="Sonar Playlist"></img>
          ) : accountBadgeVisible ? (
            <AccountHeaderLogo src={accountLogo} alt="Account logo" />
          ) : (
            <PlaylistIcon
              width={'100px'}
              height={'70px'}
              stroke={playlist.color}
              viewBox="0 0 17 17"
            />
          )}
        </Header>
        <ContentWrapper>
          <InfoWrapper ref={headerRef}>
            <TitleRow>
              <Tooltip
                visible={titleTooltip}
                placement="bottom"
                overlay={<span>{playlist.name}</span>}
              >
                <TitleText>{playlist.name}</TitleText>
              </Tooltip>
              {accountBadgeVisible && (
                <AccountBadgeWrapper>
                  <AccountPlaylistBadge />
                </AccountBadgeWrapper>
              )}
            </TitleRow>
            {playlist.description && (
              <Tooltip
                visible={descriptionTooltip}
                placement={descriptionTooltipPlacement}
                overlay={playlist.description}
                overlayInnerStyle={{
                  whiteSpace: 'normal',
                  maxWidth: descriptionTooltipWidth ?? undefined,
                  width: descriptionTooltipWidth ?? undefined,
                }}
              >
                {isSonarRecommended && (
                  <RecommendedDescriptionContainer>
                    <AvatarLetter
                      avatar={playlist?.created_for?.avatar}
                      name={playlist?.created_for?.first_name}
                      rounded={true}
                    />
                    {<RecommendedDescription>{playlist.description}</RecommendedDescription>}
                  </RecommendedDescriptionContainer>
                )}
                {!isSonarRecommended && (
                  <Description
                    ref={descriptionRef}
                    style={{
                      WebkitLineClamp: descriptionMaxLines,
                    }}
                  >
                    <div ref={descriptionTextRef}>{playlist.description}</div>
                  </Description>
                )}
              </Tooltip>
            )}
          </InfoWrapper>
          <PlaylistInfo>
            <DateContainer>
              <span className="playlist-card__created-by">
                <strong>Created by: </strong>
                {createdBy || (
                  <>
                    {'N/A'}
                    <Tooltip
                      placement="right"
                      overlayInnerStyle={{ maxWidth: 200 }}
                      title={
                        <span className="tooltip-text">
                          Creator information isn&apos;t available for this playlist
                        </span>
                      }
                    >
                      <InfoIcon size="small" icon="Info" />
                    </Tooltip>
                  </>
                )}
              </span>
            </DateContainer>
            <div className="playlist-card__bottom-info">
              <DateContainer className="playlist-card__dates">
                <span className="playlist-card__date-row">
                  <strong>Created: </strong>
                  {createdTime}
                </span>
                {modifiedTime && (
                  <span className="playlist-card__date-row">
                    <strong>Modified: </strong>
                    {modifiedTime}
                  </span>
                )}
              </DateContainer>
              <Content>
                <div>
                  {contentLength(
                    playlist.content || [],
                    playlist.content_count,
                    playlist.activities_count
                  )}
                </div>
                {alreadyAssigned && !allUsersAssigned ? (
                  <AssignedMessage>
                    <Icon icon="Check2" size="small" stroke={'#009D6F'} />
                    <span>{assignedLabel}</span>
                  </AssignedMessage>
                ) : (
                  <AssignedMessage>
                    {allUsersAssigned ? <span>{assignedLabel}</span> : assignedLabel}
                  </AssignedMessage>
                )}
              </Content>
            </div>
          </PlaylistInfo>
          <Divider flat={bothMaxHeight} />
          <CardFooter>
            {canShowAccountToggle && (
              <AccountPlaylistToggle
                playlistId={playlist.id}
                initialIsAccountPlaylist={isAccountPlaylist}
                isAccountOwner={isAccountOwner}
                isTeamManager={isTeamManager}
                onRefresh={handleAccountToggleRefresh}
                onStateChange={handleToggleStateChange}
                size="compact"
              />
            )}
            <ActionButtonsSlot>
              <ActionButtons
                isSonar={isSonarRecommended}
                alreadyAssigned={alreadyAssigned}
                playlist={playlist}
                onAddUsers={handleAddUsers}
                onDeletePlaylist={onDeletePlaylist}
                onEditPlaylist={onEditPlaylist}
                onClonePlaylist={onClonePlaylist}
                onAssignUser={onAssignUser}
                onUnassignUser={onUnassignUser}
                hideClone={hideClone}
                disableAddUsers={disableAddUsers}
                hideAddUsers={hideAddUsers}
                addUsersTooltip={addUsersTooltip}
                addUsersLabel={addUsersLabel}
              />
            </ActionButtonsSlot>
          </CardFooter>
        </ContentWrapper>
      </Container>
    </Tooltip>
  );
};

export default PlaylistCard;
